import { ConfigProvider } from 'antd';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { baseTheme } from '@src/theme';
export { withTheme } from './withTheme';

// Create theme context
type ThemeContextType = {
  theme: any;
  setTheme: (theme: any) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  theme: baseTheme,
  setTheme: () => {},
});

// Hook to use theme
export const useTheme = () => useContext(ThemeContext);

export interface ThemeProviderProps {
  children: React.ReactNode;
  initialTheme?: any;
  onThemeChange?: (themeSetter: (theme: any) => void) => void;
}

// Add compatibility layer for old theme structure
const addCompatibilityLayer = (theme: any) => {
  if (!theme.extend) {
    theme.extend = {
      colors: {
        ...theme.colors.primary.main,
        ...theme.colors.secondary.main,
        ...theme.colors.success.main,
        ...theme.colors.warning.main,
        ...theme.colors.error.main,
        ...theme.colors.info.main,
        primaryBlue: theme.colors.primary.main.filled,
        primaryBlack: theme.colors.secondary.main.text,
        primaryWhite: '#ffffff',
        primaryGreen: theme.colors.success.main.filled,
        primaryYellow: theme.colors.warning.main.filled,
        primaryRed: theme.colors.error.main.filled,
      },
    };
  }
  return theme;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialTheme = baseTheme,
  onThemeChange,
}) => {
  const [theme, setTheme] = useState(addCompatibilityLayer(initialTheme));

  // Provide the setTheme function to parent components if needed
  useEffect(() => {
    if (onThemeChange) {
      onThemeChange((newTheme: any) => setTheme(addCompatibilityLayer(newTheme)));
    }
  }, [onThemeChange]);

  // Create antd theme config
  const antdThemeConfig = {
    token: {
      colorPrimary: theme.colors.primary.main.filled,
      // Add more antd token overrides based on theme
    },
    components: {
      Button: {
        colorPrimary: theme.colors.primary.main.filled,
        colorPrimaryHover: theme.colors.primary.main.hover,
        // Add more component-specific theming
      },
      // Add theme overrides for other antd components
    },
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <ConfigProvider theme={antdThemeConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
};
